import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { Stack } from '@mui/material';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { FlowPlaylistMediaWidget } from '@/components/flows/detail/edit-details/flow-playlist-media-widget';
import { SessionPlaylistMediaWidget } from '@/components/sessions/session-playlist-media-widget';
import { SessionActors } from '@/components/events/detail/tabs/sessions/session-actors/session-contributor-widget';
import { getObjectId } from '@/helpers/object.id.helper';
import { useMemo } from 'react';
import { useGetSessionQuery } from '@/state/queries/sessions/use-get-session';
import {
  isEmbeddedDraftLayerSession,
  pickNewerSession,
  resolveSessionOriginIdFromFlowSlot,
} from '@/components/flows/detail/flow-detail.utils';
import FlowPlaceholder from './flow-placeholder';
import { Moderators } from '@/components/events/detail/tabs/flows/moderators/moderator-widget';
import { useSearchParams } from 'react-router';
import type { ContributorListDisplayVariant } from '../flow-detail.types';
import type { Mode } from '@/types/entity-mode';
import { ServerError } from '@/types/errors/server.error';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';

const SLOT_ASSETS_CONTRIBUTOR_VARIANT: ContributorListDisplayVariant = 'flowSlotAssets';

/** Shows actor and media widgets for the selected slot; respects draft vs contributor list mode. */
export default function SlotAssetsDetails({
  selectedSlot,
  slotTitle,
  listViewMode: listViewModeProp,
}: {
  selectedSlot: FlowSlotDto | undefined;
  slotTitle: string;
  /** Draft (PLAN) vs contributor (ORGANIZE) slot-assets list mode; defaults from store when omitted. */
  listViewMode?: Mode;
}) {
  const [searchParams] = useSearchParams();
  const eventSlotIdFromUrl = searchParams.get('eventSlotId');
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowFromStore = useFlowStore((s) => s.flow);
  const listViewMode = useMemo(
    () => listViewModeProp ?? (flowOrganize && !flowPlan ? 'ORGANIZE' : 'PLAN'),
    [listViewModeProp, flowOrganize, flowPlan],
  );

  const flow = useMemo(() => {
    if (listViewMode === 'ORGANIZE' && flowOrganize) return flowOrganize;
    return flowPlan ?? flowOrganize ?? flowFromStore;
  }, [listViewMode, flowOrganize, flowPlan, flowFromStore]);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);

  const sessionOriginId = useMemo(
    () =>
      resolveSessionOriginIdFromFlowSlot(selectedSlot, {
        eventSessionsPlan,
        eventSessionsOrganize,
        flowPlan,
      }),
    [selectedSlot, eventSessionsPlan, eventSessionsOrganize, flowPlan],
  );

  const planSessionFromStore = useMemo(() => {
    if (!sessionOriginId) return undefined;
    return eventSessionsPlan?.find((s) => {
      const sOriginId = s.origin ? getObjectId(s.origin) : s.id;
      return sOriginId === sessionOriginId;
    });
  }, [sessionOriginId, eventSessionsPlan]);

  const organizeSessionFromStore = useMemo(() => {
    if (!sessionOriginId) return undefined;
    return eventSessionsOrganize?.find((s) => {
      const sOriginId = s.origin ? getObjectId(s.origin) : s.id;
      return sOriginId === sessionOriginId;
    });
  }, [sessionOriginId, eventSessionsOrganize]);

  const shouldFetchFromBackend = !!sessionOriginId;

  const { data: sessionFromBackendPlan, error: sessionFromBackendPlanError } = useGetSessionQuery(
    shouldFetchFromBackend ? sessionOriginId : undefined,
    'PLAN',
  );

  const planNotFound =
    listViewMode === 'PLAN' &&
    sessionFromBackendPlanError instanceof ServerError &&
    sessionFromBackendPlanError.statusCode === 404;

  const shouldFetchOrganize =
    shouldFetchFromBackend &&
    (listViewMode === 'ORGANIZE' || listViewMode === 'PERFORM' || (planNotFound && !!organizeSessionFromStore));

  const { data: sessionFromBackendOrganize } = useGetSessionQuery(
    shouldFetchOrganize ? sessionOriginId : undefined,
    'ORGANIZE',
  );

  const sessionPlan = useMemo(
    () => pickNewerSession(planSessionFromStore, sessionFromBackendPlan),
    [planSessionFromStore, sessionFromBackendPlan],
  );
  const sessionOrganize = useMemo(
    () => pickNewerSession(organizeSessionFromStore, sessionFromBackendOrganize),
    [organizeSessionFromStore, sessionFromBackendOrganize],
  );

  const slotSession = useMemo((): SessionDto | undefined => {
    if (selectedSlot?.contentType !== 'Session' || !selectedSlot.content || typeof selectedSlot.content !== 'object') {
      return undefined;
    }
    const embedded = selectedSlot.content as SessionDto;
    const backendMode = listViewMode === 'PLAN' ? 'PLAN' : 'ORGANIZE';
    const allowOrganizeFallback = listViewMode === 'PLAN' && planNotFound;
    if (
      embedded.mode === backendMode ||
      (allowOrganizeFallback && embedded.mode === 'ORGANIZE') ||
      (listViewMode === 'PLAN' && isEmbeddedDraftLayerSession(embedded.mode))
    ) {
      return embedded;
    }
    return undefined;
  }, [selectedSlot, listViewMode, planNotFound]);

  const session = useMemo(() => {
    if (listViewMode === 'PLAN') {
      return sessionPlan ?? slotSession ?? sessionOrganize;
    }
    return sessionOrganize ?? sessionPlan ?? slotSession;
  }, [listViewMode, sessionOrganize, sessionPlan, slotSession]);

  const sessionAssetsMode: Mode =
    listViewMode === 'ORGANIZE' || listViewMode === 'PERFORM' ? (sessionOrganize ? 'ORGANIZE' : 'PLAN') : 'PLAN';

  if (!flow) return null;

  if (!selectedSlot) {
    return (
      <Stack spacing={2}>
        <Moderators
          flow={flow}
          eventSlotId={eventSlotIdFromUrl ?? undefined}
          displayVariant={SLOT_ASSETS_CONTRIBUTOR_VARIANT}
        />
        <FlowPlaylistMediaWidget flow={flow} scope="pre_flow" liveReady={flow.liveContext?.isRunning ?? false} />
        <FlowPlaylistMediaWidget flow={flow} scope="flow_intro" liveReady={flow.liveContext?.isRunning ?? false} />
        <FlowPlaylistMediaWidget flow={flow} scope="flow_transition" liveReady={flow.liveContext?.isRunning ?? false} />
        <FlowPlaylistMediaWidget flow={flow} scope="flow_resume" liveReady={flow.liveContext?.isRunning ?? false} />
      </Stack>
    );
  }

  if (selectedSlot.contentType === 'Session') {
    if (!session) return null;
    return (
      <Stack spacing={2}>
        <SessionActors
          session={session}
          modeOverride={sessionAssetsMode}
          displayVariant={SLOT_ASSETS_CONTRIBUTOR_VARIANT}
        />
        <SessionPlaylistMediaWidget
          session={session}
          context="Session"
          contextParentId={flow?.origin ? getObjectId(flow.origin) : flow.id}
          contextParentType="Flow"
          enablePlayerSocket={false}
          enabled={false}
        />
      </Stack>
    );
  }

  if (selectedSlot.contentType === 'Break') {
    return (
      <Stack spacing={2}>
        <FlowPlaylistMediaWidget
          flow={flow}
          scope="slot"
          slotId={selectedSlot.id}
          slotTitle={slotTitle}
          liveReady={flow.liveContext?.isRunning ?? false}
        />
      </Stack>
    );
  }

  return <FlowPlaceholder title={slotTitle} />;
}
